import * as ko from 'knockout';
import {ObjectTypeId} from '../../../constants';
import {getHash} from '../../../constants/RouteDefinitions';
import {PLEASE_WAIT_MESSAGE} from '../../../constants/Language';
import {IObjectData, IOverride} from '../../../index';
import {getObjectDefinition, getSyncFieldValue} from '../../../util/misc';
import {nsGroupMemberOverride} from '../../group/members/nsGroupMemberOverride';
import {nsOverride} from '../../override/nsOverride';
import {nsUserMembership} from './nsUserMembership';
import canMutateMembershipOverride = nsGroupMemberOverride.canMutateMembershipOverride;
import canShowMembershipOverrideAction = nsGroupMemberOverride.canShowMembershipOverrideAction;
import getMembershipOverrideAction = nsGroupMemberOverride.getMembershipOverrideAction;
import isMembershipOverrideDeleted = nsGroupMemberOverride.isMembershipOverrideDeleted;
import isOverrideEffectivelyRemoved = nsOverride.isOverrideEffectivelyRemoved;

/** Row VM dependencies on {@link MembershipList} (avoids circular import). */
export interface IUserMembershipListParent {
    onGroupRowActionClick(group: IObjectData): void;
    getOverrideForGroup(group: IObjectData): IOverride | null;
    memberActionsEnabled: KnockoutComputed<boolean>;
    updateOverrideComment(overrideId: number, comment: string): Promise<void>;
}

/** Expandable grid row for one group membership. */
export class MembershipRowVm {
    readonly isExpanded = ko.observable(false);
    readonly isEditingOverrideComment = ko.observable(false);
    readonly isSavingOverrideDetails = ko.observable(false);
    readonly overrideCommentDraft = ko.observable('');
    readonly overridePanelRevision = ko.observable(0);
    readonly overrideDetailsWaitMessage = PLEASE_WAIT_MESSAGE;

    constructor(
        group: IObjectData,
        readonly parentList: IUserMembershipListParent,
    ) {
        this.group = group;
    }

    group: IObjectData;

    setGroup(group: IObjectData): void {
        this.group = group;
        this.bumpOverridePanelRevision();
    }

    getRowBindings() {
        return {
            attr: {'aria-expanded': this.isExpanded},
        };
    }

    hasOverride(): boolean {
        return !!this.getOverride();
    }

    doExpandToggle = (): void => {
        if (this.hasOverride()) {
            this.isExpanded(!this.isExpanded());
        }
    };

    getOverride() {
        return this.parentList.getOverrideForGroup(this.group);
    }

    /** Plain row action kind from override state (or remove when no override). */
    rowActionKind(): nsGroupMemberOverride.MembershipMemberActionKind {
        const override = this.getOverride();
        return override ? getMembershipOverrideAction(override).actionKind : 'remove';
    }

    canRunRowAction(): boolean {
        return this.parentList.memberActionsEnabled();
    }

    onRowActionClick = (): void => {
        this.parentList.onGroupRowActionClick(this.group);
    };

    /** Display params for shared `component/override/override-status`. */
    getOverrideStatusParams() {
        const override = this.getOverride();
        if (!override) {
            return null;
        }
        const display = nsUserMembership.getUserMembershipOverrideDisplay(override);
        return {
            translationKey: display.translationKey,
            pillVariant: display.pillVariant,
            hasComment: !!override.comment?.trim(),
        };
    }

    /** Same CMS key as the status pill (pending/synced included). */
    getOverrideSectionHeadingKey(): string | null {
        return this.getOverrideStatusParams()?.translationKey ?? null;
    }

    nameText(): string {
        return getSyncFieldValue('Name', this.group.syncfields) ?? '';
    }

    descriptionText(): string {
        return getSyncFieldValue('Description', this.group.syncfields) ?? '';
    }

    groupHref(): string | null {
        if (!this.group.id) {
            return null;
        }
        return getHash(getObjectDefinition(ObjectTypeId.Group)?.routeDetails ?? 'groupdetails', {id: this.group.id});
    }

    hasGroupLink(): boolean {
        return !!this.groupHref();
    }

    overrideIdText(): string {
        const id = this.getOverride()?.id;
        return id == null ? '' : String(id);
    }

    overrideValueText(): string {
        return this.getOverride()?.value ?? '';
    }

    overrideSyncImportFileIdText(): string {
        const id = this.getOverride()?.syncronizedimportfileid;
        return id ? String(id) : '';
    }

    overrideCommentText(): string {
        return this.getOverride()?.comment ?? '';
    }

    overrideOverriddenWhen(): string {
        return this.getOverride()?.overriddenwhen ?? '';
    }

    overrideOverriddenByName(): string {
        return this.getOverride()?.overriddenby ?? '';
    }

    overrideDeletedWhen(): string {
        return this.getOverride()?.deletedwhen ?? '';
    }

    overrideDeletedByName(): string {
        return this.getOverride()?.deletedby ?? '';
    }

    isOverrideDeleted(): boolean {
        const override = this.getOverride();
        return !!override && isMembershipOverrideDeleted(override);
    }

    /** Active remove or soft-deleted add — expanded row uses the danger accent. */
    isEffectivelyRemoved(): boolean {
        return isOverrideEffectivelyRemoved(this.getOverride());
    }

    canEditOverrideComment(): boolean {
        const override = this.getOverride();
        return !!override && canMutateMembershipOverride(override);
    }

    startEditOverrideComment = (): void => {
        this.overrideCommentDraft(this.overrideCommentText());
        this.isEditingOverrideComment(true);
    };

    cancelEditOverrideComment = (): void => {
        this.isEditingOverrideComment(false);
        this.overrideCommentDraft(this.overrideCommentText());
    };

    submitOverrideComment = (): void => {
        const override = this.getOverride();
        if (!override?.id) {
            return;
        }
        this.isEditingOverrideComment(false);
        this.isSavingOverrideDetails(true);
        void this.parentList
            .updateOverrideComment(override.id, this.overrideCommentDraft())
            .finally(() => this.isSavingOverrideDetails(false));
    };

    /** Notify the expanded panel after this row's override data is replaced. */
    bumpOverridePanelRevision(): void {
        this.overridePanelRevision(this.overridePanelRevision() + 1);
    }

    showRowAction(): boolean {
        const override = this.getOverride();
        return !override || canShowMembershipOverrideAction(override);
    }
}
